import {
  type Api,
  type BaseQueryFn,
  type CoreModule,
  type EndpointDefinitions,
  type InfiniteData,
} from '@reduxjs/toolkit/query';

import {
  type AllQueryKeys,
  type InfiniteQueryDefinitionType,
  type MaybeDrafted,
  type MutationLifecycleApi,
  type QueryArgsFrom,
} from './types';

/**
 * Composes an update recipe for `infiniteQueries`. Calls `updateRecipe` on every
 * page of the infinite data cache until instructed to break by the `updateRecipe`.
 */
export const createUpdateInfiniteDataRecipe = <T>(
  updateRecipe: (page: T) => boolean,
) => {
  return (draft: InfiniteData<T, unknown>) => {
    for (const page of draft.pages) {
      const shouldBreak = updateRecipe(page);
      if (shouldBreak) break;
    }
  };
};

type ObjectWithItems = { items: unknown[] };

interface CreateUpdateItemRecipeProps<T extends ObjectWithItems> {
  predicate: (item: T['items'][number]) => boolean;
  updateRecipe: (item: T['items'][number]) => void;
}

/**
 * Composes an update recipe for updating a **SINGLE** item within some object
 * containing a list of `items`.
 */
export const createUpdateSingleItemRecipe = <T extends ObjectWithItems>({
  predicate,
  updateRecipe,
}: CreateUpdateItemRecipeProps<T>) => {
  return (draft: T) => {
    // IIFE is here only to maintain purity of the for loop
    return (() => {
      for (const item of draft.items) {
        if (predicate(item)) {
          updateRecipe(item);
          return true;
        }
      }
      return false;
    })();
  };
};

/**
 * Composes an update recipe for updating **MULTIPLE** items within some object
 * containing a list of `items`.
 */
export const createUpdateMultipleItemsRecipe = <T extends ObjectWithItems>({
  predicate,
  updateRecipe,
}: CreateUpdateItemRecipeProps<T>) => {
  return (draft: T) => {
    // IIFE is here only to maintain purity of the for loop
    return (() => {
      for (const item of draft.items) {
        if (predicate(item)) {
          updateRecipe(item);
        }
      }
      return false;
    })();
  };
};

/**
 * Creates a function that encapsulates the logic to `dispatch` `updateQueryData` on all
 * cache entries of a specific query endpoint.
 *
 * @param api Your RTK Query `api` instance
 * @param endpointName The name of a query endpoint defined in your `api` instance.
 * @param mutationLifecycleApi The `MutationLifecycleApi` object provided to `onQueryStarted` callbacks - carrying `getState` and `dispatch` methods.
 * @param includePredicate An optional predicate function to only include certain cache entries to be updated.
 *
 * @example
 * // within an `onQueryStarted` callback
 * onQueryStarted: async (newItem, mutationLifecycleApi) => {
 *  // create your updater function
 *  const updateAllCacheEntries = createAllCacheEntriesUpdater(
 *    api,
 *    'getItems',
 *    mutationLifecycleApi,
 *  )
 *
 *  // and call it with your update recipe - the recipe will be dispatched on all cache entries
 *  const  = updateAllCacheEntries((draft) => {
 *    draft.push(newItem);
 *  })
 * }
 */
export const createAllCacheEntriesUpdater = <
  BaseQuery extends BaseQueryFn,
  Definitions extends EndpointDefinitions,
  ReducerPath extends string,
  TagTypes extends string,
  EndpointName extends AllQueryKeys<Definitions>,
>(
  api: Api<BaseQuery, Definitions, ReducerPath, TagTypes, CoreModule>,
  endpointName: EndpointName,
  {
    getState,
    dispatch,
  }: Pick<
    MutationLifecycleApi<Definitions, BaseQuery, ReducerPath>,
    'getState' | 'dispatch'
  >,
  includePredicate: (
    queryArgs: QueryArgsFrom<Definitions, EndpointName>,
  ) => boolean = () => true,
) => {
  type DataDraft = NonNullable<
    Definitions[EndpointName]['Types']
  >['ResultType'];
  type InfiniteDataDraft = InfiniteData<DataDraft, unknown>;
  type RecipeDraft =
    Definitions[EndpointName] extends InfiniteQueryDefinitionType
      ? InfiniteDataDraft
      : DataDraft;

  return (updateRecipe: (draft: MaybeDrafted<RecipeDraft>) => void) => {
    const queryArgs = api.util.selectCachedArgsForQuery(
      getState(),
      endpointName,
    ) as NonNullable<Definitions[EndpointName]['Types']>['QueryArg'][];
    return queryArgs
      .filter(includePredicate)
      .map((args) =>
        dispatch(api.util.updateQueryData(endpointName, args, updateRecipe)),
      );
  };
};
